import { useEffect, useState, useRef, createContext, useContext } from 'react';
import { Loader2 } from 'lucide-react';
import { AUTH_CONFIG } from '../../config/constants';
import { SystemMessagesContext } from '../../context/SystemMessagesContext';

interface AuthCheckProps {
  children: (authContext: { roleFunctionalities: RoleFunctionality[]; updateAuthContext: () => Promise<void>; }) => JSX.Element;
}

interface RoleFunctionality {
  Functionality: string;
  Role: string;
  ScreenName: string;
}

interface SystemMessages {
  Name: string;
  Value: string;
  DataType: string;
}

interface SystemConfig {
  Id: string;
  Value: string;
}

interface UserRoles {
  ADGroups: string[];
  CallerApplication: string;
  Division: string;
  IsAuthenticated: boolean;
  IsAuthorized: boolean;
  NetID: string;
  Program: string;
  RoleFunctionalities: RoleFunctionality[];
  Roles: string[];
  ssoToken: string | null;
}

// Create AuthContext
export const AuthContext = createContext<{
  roleFunctionalities: RoleFunctionality[];
  updateAuthContext: () => Promise<void>;
}>({
  roleFunctionalities: [],
  updateAuthContext: async () => {},
});

// Hook for using AuthContext
export const useAuth = () => useContext(AuthContext);

const CACHE_DURATION = 2 * 24 * 60 * 60 * 1000; // 2 days

const getCachedData = (key: string) => {
  const item = sessionStorage.getItem(key);
  if (!item) return null;

  const { value, timestamp } = JSON.parse(item);
  if (Date.now() - timestamp > CACHE_DURATION) {
    sessionStorage.removeItem(key);
    return null;
  }
  return value;
};

const setCachedData = (key: string, value: any) => {
  const data = {
    value,
    timestamp: Date.now()
  };
  sessionStorage.setItem(key, JSON.stringify(data));
};

export const AuthCheck = ({ children }: AuthCheckProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [userRoles, setUserRoles] = useState<UserRoles>();
  const [userDetails, setUserDetails] = useState<any>();
  const [roleFunctionalities, setRoleFunctionalities] = useState<RoleFunctionality[]>([]);
  const [systemMessages, setSystemMessages] = useState<SystemMessages[]>([]);
  const [systemConfig, setSystemConfig] = useState<SystemConfig[]>([]);
  const apiCallsInProgress = useRef(false);

  const fetchSystemConfig = async () => {
    const cachedConfig = getCachedData('systemConfig');
    if (cachedConfig) {
      setSystemConfig(cachedConfig);
      return;
    }

    try {
      console.log('Fetching system config...');
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/api/config/sys-config`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSystemConfig(data);
        setCachedData('systemConfig', data);
      } else {
        console.error('Failed to fetch system config');
      }
    } catch (error) {
      console.error('Error fetching system config:', error);
    }
  };

  const fetchSystemMessages = async () => {
    const cachedMessages = getCachedData('systemMessages');
    if (cachedMessages) {
      setSystemMessages(cachedMessages);
      return;
    }

    try {
      console.log('Fetching system messages...');
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/api/config/system-messages`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSystemMessages(data);
        setCachedData('systemMessages', data);
      } else {
        console.error('Failed to fetch system messages');
      }
    } catch (error) {
      console.error('Error fetching system messages:', error);
    }
  };

  const fetchRoles = async () => {
    const cachedRoles = getCachedData('userRoles');
    if (cachedRoles) {
      setUserRoles(cachedRoles);
      setRoleFunctionalities(cachedRoles.RoleFunctionalities);
      setUserDetails(localStorage.getItem('fullName'));
      return;
    }

    try {
      console.log('Fetching roles...');
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/api/auth/roles`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        setUserRoles(data.data);
        setRoleFunctionalities(data.data.RoleFunctionalities);
        setUserDetails(data.user.FullName);
        
        setCachedData('userRoles', data.data);
        
        if (data.user.FullName) {
          localStorage.setItem('fullName', data.user.FullName);
          localStorage.setItem('netId', data.data.NetID);
        }
        
        localStorage.setItem('roleFunctionalities', JSON.stringify(data.data.RoleFunctionalities));
      } else {
        console.error('Failed to fetch user roles');
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  // Function to update auth context - this will be passed down
  const updateAuthContext = async () => {
    // Clear existing cache to force fresh data
    sessionStorage.removeItem('userRoles');
    sessionStorage.removeItem('systemMessages');
    sessionStorage.removeItem('systemConfig');
    
    // Fetch fresh data
    await Promise.all([fetchRoles(), fetchSystemMessages(), fetchSystemConfig()]);
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (apiCallsInProgress.current) return;
      apiCallsInProgress.current = true;

      try {
        const baseUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${baseUrl}/api/auth/verify`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (!data.authenticated) {
            console.error('Auth verification failed');
            window.location.href = `${AUTH_CONFIG.REACT_APP_LOGIN_URL}`;
          } else {
            setIsAuthenticated(true);
            await Promise.all([fetchRoles(), fetchSystemMessages(), fetchSystemConfig()]);
          }
        } else {
          console.error('Auth verification failed');
          window.location.href = `${AUTH_CONFIG.REACT_APP_LOGIN_URL}`;
        }
      } catch (error) {
        console.error('Auth verification error:', error);
        window.location.href = `${AUTH_CONFIG.REACT_APP_LOGIN_URL}`;
      } finally {
        setIsChecking(false);
        apiCallsInProgress.current = false;
      }
    };

    checkAuth();

    return () => {
      apiCallsInProgress.current = false;
    };
  }, []);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return isAuthenticated ? (
    <SystemMessagesContext.Provider value={{ 
      systemMessages, 
      setSystemMessages,
      systemConfig,
      setSystemConfig 
    }}>
      {children({ roleFunctionalities, updateAuthContext })}
    </SystemMessagesContext.Provider>
  ) : null;
};

export default AuthCheck;

export const hasImpersonationFunctionality = (): boolean => {
  try {
    const storedFunctionalities = localStorage.getItem('roleFunctionalities');
    
    if (!storedFunctionalities) {
      return false;
    }
    
    const roleFunctionalities = JSON.parse(storedFunctionalities);
    
    return roleFunctionalities.some(
      (func: { Functionality: string }) => func.Functionality === 'Impersonation'
    );
  } catch (error) {
    console.error('Error checking impersonation functionality:', error);
    return false;
  }
};

export const hasImpersonated = (): boolean => {
  try {
    const impersonatedBy = localStorage.getItem('impersonatedBy');
    console.log(impersonatedBy)
    
    if (!impersonatedBy) {
      return false;
    }  
    else {
      return true
    } 
     
  }
  catch (error) {
    console.error('Error checking impersonation functionality:', error);
    return false;
  }
};
